import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { PageShell } from '../components/stage/PageShell.js';
import {
  HOME_PREFS_DEFAULT,
  type HomeLayout,
  type HomePrefs,
  type RailKey,
  type RailMode,
  sameHomePrefs,
  useHomePrefs,
  writeHomePrefsLocal,
} from '../lib/homePrefs.js';
import { readHomeRule, setHomeRule, useHomeMode, type HomeModeRule } from '../lib/homeMode.js';
import { hapticTap } from '../lib/native.js';
import { useProfile, useUpdateProfile } from '../lib/useProfile.js';
import './atalhos-inicio.css';

const LABEL: Record<RailKey, { label: string; art: string }> = {
  agenda_day: { label: 'Agenda', art: '/digest-icons/agenda-compromisso.webp' },
  bills: { label: 'Dinheiro', art: '/habilidades/contas.webp' },
  people: { label: 'Pessoas', art: '/digest-icons/contato-novo.webp' },
  deliveries: { label: 'Documentos', art: '/menu/entregas.webp' },
};

const MODES: { key: RailMode; title: string; line: string }[] = [
  {
    key: 'visible',
    title: 'Visíveis',
    line: 'Os quatro atalhos sempre à vista, como vêm de fábrica.',
  },
  {
    key: 'discreet',
    title: 'Discretos',
    line: 'Ficam mais leves e menores no rosto; os nomes continuam legíveis.',
  },
  {
    key: 'clean',
    title: 'Tela limpa',
    line: 'Só o rosto. Sem nomes, sem Hoje: uma seta discreta na lateral traz os quatro atalhos.',
  },
];

/** Para quem tem clínica: em que dia a tela inicial abre (antes ficava solto em Ajustes › Você). */
const RULES: { key: HomeModeRule; title: string; line: string }[] = [
  {
    key: 'horario',
    title: 'Pelo horário',
    line: 'No dia da clínica durante o atendimento e no seu dia pessoal fora dele.',
  },
  {
    key: 'pessoal',
    title: 'Sempre no pessoal',
    line: 'O seletor do alto troca quando você quiser.',
  },
  {
    key: 'clinica',
    title: 'Sempre na clínica',
    line: 'O seletor do alto troca quando você quiser.',
  },
];

const LAYOUTS: { key: HomeLayout; title: string; line: string }[] = [
  {
    key: 'hoje',
    title: 'Com o Hoje aberto',
    line: 'Eu fico no alto e o Hoje fica aberto embaixo, com os atalhos. Puxe para ver tudo.',
  },
  {
    key: 'palco',
    title: 'Personagem em tela cheia',
    line: 'Eu ocupo a tela; os atalhos ficam ao meu lado e o Hoje abre quando você puxa.',
  },
];

/**
 * Tela inicial: como abre (o Hoje aberto ou o personagem em tela cheia), em que dia abre para
 * quem tem clínica, e os atalhos: como aparecem, de que lado e em que ordem. Tudo por opção
 * com nome e com prévia, salvo na hora. Nada aqui muda a navegação: os quatro
 * atalhos continuam existindo e tudo também abre pela voz.
 */
export function AtalhosInicioPage() {
  const [, navigate] = useLocation();
  const saved = useHomePrefs();
  const profile = useProfile().data;
  const update = useUpdateProfile();
  const [prefs, setPrefs] = useState<HomePrefs>(saved);
  const { available: hasClinic } = useHomeMode();
  const [rule, setRule] = useState<HomeModeRule>(readHomeRule);
  useEffect(() => {
    setPrefs((current) => (sameHomePrefs(current, saved) ? current : saved));
  }, [saved]);

  const apply = (next: HomePrefs) => {
    hapticTap();
    setPrefs(next);
    writeHomePrefsLocal(next);
    update.mutate({ preferences: { ...(profile?.preferences ?? {}), home: next } });
  };
  const move = (key: RailKey, delta: -1 | 1) => {
    const index = prefs.order.indexOf(key);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= prefs.order.length) return;
    const order = [...prefs.order];
    order.splice(index, 1);
    order.splice(target, 0, key);
    apply({ ...prefs, order });
  };
  const isDefault = sameHomePrefs(prefs, HOME_PREFS_DEFAULT);

  return (
    <PageShell
      kicker="Ajustes"
      title="Tela inicial"
      lead="Como ela abre e os atalhos. Tudo que está neles eu também abro quando você pede."
      onBack={() => navigate('/ajustes')}
      backLabel="Voltar aos Ajustes"
    >
      <section className="pgs-section">
        <h2 className="pgs-h2">Como a tela inicial abre</h2>
        <div className="ati-modes" role="radiogroup" aria-label="Como a tela inicial abre">
          {LAYOUTS.map((layout) => (
            <button
              key={layout.key}
              type="button"
              role="radio"
              aria-checked={prefs.layout === layout.key}
              className={`pgs-row ati-mode${prefs.layout === layout.key ? ' on' : ''}`}
              onClick={() => apply({ ...prefs, layout: layout.key })}
            >
              <span className="pgs-rowt">
                <b>{layout.title}</b>
                <small>{layout.line}</small>
              </span>
              <span className="ati-radio" />
            </button>
          ))}
        </div>
      </section>

      {hasClinic && (
        <section className="pgs-section">
          <h2 className="pgs-h2">Em que dia abre</h2>
          <div className="ati-modes" role="radiogroup" aria-label="Em que dia a tela inicial abre">
            {RULES.map((item) => (
              <button
                key={item.key}
                type="button"
                role="radio"
                aria-checked={rule === item.key}
                className={`pgs-row ati-mode${rule === item.key ? ' on' : ''}`}
                onClick={() => {
                  hapticTap();
                  setHomeRule(item.key);
                  setRule(item.key);
                }}
              >
                <span className="pgs-rowt">
                  <b>{item.title}</b>
                  <small>{item.line}</small>
                </span>
                <span className="ati-radio" />
              </button>
            ))}
          </div>
        </section>
      )}

      {prefs.layout === 'palco' && (
        <>
          <div className={`ati-preview mode-${prefs.rail} side-${prefs.side}`} aria-hidden="true">
            <span className="ati-face" />
            <span className="ati-rail">
              {prefs.order.map((key) => (
                <i key={key}>
                  <img src={LABEL[key].art} alt="" />
                </i>
              ))}
            </span>
            {prefs.rail !== 'clean' && <span className="ati-hoje">Hoje</span>}
            {prefs.rail === 'clean' && <span className="ati-tab" />}
          </div>

          <section className="pgs-section">
            <h2 className="pgs-h2">Como aparecem</h2>
            <div className="ati-modes" role="radiogroup" aria-label="Como os atalhos aparecem">
              {MODES.map((mode) => (
                <button
                  key={mode.key}
                  type="button"
                  role="radio"
                  aria-checked={prefs.rail === mode.key}
                  className={`pgs-row ati-mode${prefs.rail === mode.key ? ' on' : ''}`}
                  onClick={() => apply({ ...prefs, rail: mode.key })}
                >
                  <span className="pgs-rowt">
                    <b>{mode.title}</b>
                    <small>{mode.line}</small>
                  </span>
                  <span className="ati-radio" />
                </button>
              ))}
            </div>
          </section>

          {prefs.rail === 'discreet' && (
            <section className="pgs-section">
              <h2 className="pgs-h2">Nomes dos botões</h2>
              <div className="pgs-chips" role="radiogroup" aria-label="Nomes dos botões">
                <button
                  type="button"
                  role="radio"
                  aria-checked={prefs.labels}
                  className={`pgs-chip${prefs.labels ? ' on' : ''}`}
                  onClick={() => apply({ ...prefs, labels: true })}
                >
                  Mostrar
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!prefs.labels}
                  className={`pgs-chip${!prefs.labels ? ' on' : ''}`}
                  onClick={() => apply({ ...prefs, labels: false })}
                >
                  Esconder
                </button>
              </div>
              <p className="pgs-note">
                Sem os nomes a tela fica mais limpa; eu continuo lendo cada botão em voz alta se
                você pedir.
              </p>
            </section>
          )}

          <section className="pgs-section">
            <h2 className="pgs-h2">De que lado</h2>
            <div className="pgs-chips" role="radiogroup" aria-label="Lado dos atalhos">
              <button
                type="button"
                role="radio"
                aria-checked={prefs.side === 'right'}
                className={`pgs-chip${prefs.side === 'right' ? ' on' : ''}`}
                onClick={() => apply({ ...prefs, side: 'right' })}
              >
                Direita
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={prefs.side === 'left'}
                className={`pgs-chip${prefs.side === 'left' ? ' on' : ''}`}
                onClick={() => apply({ ...prefs, side: 'left' })}
              >
                Esquerda
              </button>
            </div>
            <p className="pgs-note">Canhoto? Do lado esquerdo o polegar chega sem esforço.</p>
          </section>
        </>
      )}

      <section className="pgs-section">
        <h2 className="pgs-h2">A ordem dos primeiros atalhos</h2>
        <p className="ati-note">Os outros vêm depois deles, na mesma faixa.</p>
        <div className="ati-order">
          {prefs.order.map((key, index) => (
            <div key={key} className="pgs-row ati-item">
              <img className="ati-art" src={LABEL[key].art} alt="" />
              <span className="pgs-rowt">
                <b>{LABEL[key].label}</b>
              </span>
              <span className="ati-arrows">
                <button
                  type="button"
                  aria-label={`Subir ${LABEL[key].label}`}
                  disabled={index === 0}
                  onClick={() => move(key, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`Descer ${LABEL[key].label}`}
                  disabled={index === prefs.order.length - 1}
                  onClick={() => move(key, 1)}
                >
                  ↓
                </button>
              </span>
            </div>
          ))}
        </div>
      </section>

      <div className="pgs-actions">
        <button
          type="button"
          className="pgs-btn"
          disabled={isDefault}
          onClick={() => apply(HOME_PREFS_DEFAULT)}
        >
          Voltar ao padrão
        </button>
      </div>
      {update.isError && (
        <p className="pgs-note">
          Não consegui guardar no servidor agora; vale neste aparelho e tento de novo depois.
        </p>
      )}
    </PageShell>
  );
}
